"use client";

import { useMemo, useState } from "react";

function toCsv(rows) {
  if (!rows?.length) return "timestamp,action,item,user\n";
  const escape = (value) => {
    const raw = String(value ?? "");
    if (/[",\n\r]/.test(raw)) return `"${raw.replace(/"/g, '""')}"`;
    return raw;
  };
  const header = "timestamp,action,item,user";
  const body = rows.map((r) =>
    [
      escape(r.created_at || ""),
      escape(r.action || ""),
      escape(r.entity_id || ""),
      escape(r.user_id || ""),
    ].join(","),
  );
  return [header, ...body].join("\n");
}

export function AuditLogPanel({ rows = [], medicationId, limit = 50 }) {
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState("all");

  const filtered = useMemo(() => {
    let next = Array.isArray(rows) ? rows : [];
    if (medicationId) next = next.filter((r) => String(r.entity_id || "") === String(medicationId));
    if (actionFilter !== "all") next = next.filter((r) => r.action === actionFilter);
    return next;
  }, [rows, medicationId, actionFilter]);

  const pageSize = 25;
  const pageCount = Math.max(1, Math.ceil(Math.min(filtered.length, limit) / pageSize));
  const boundedPage = Math.min(page, pageCount);
  const visible = filtered.slice((boundedPage - 1) * pageSize, boundedPage * pageSize);
  const actionOptions = Array.from(new Set((rows || []).map((r) => r.action).filter(Boolean)));

  const downloadCsv = () => {
    const csv = toCsv(filtered.slice(0, limit));
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `expirydesk-audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap", marginBottom: "0.65rem" }}>
        <select
          value={actionFilter}
          onChange={(e) => {
            setActionFilter(e.target.value);
            setPage(1);
          }}
          style={{ borderRadius: 10, border: "1px solid rgba(148, 163, 184, 0.35)", padding: "0.4rem 0.6rem", fontSize: "0.8rem" }}
        >
          <option value="all">All actions</option>
          {actionOptions.map((action) => (
            <option key={action} value={action}>
              {action}
            </option>
          ))}
        </select>
        <button type="button" className="ed-btn-secondary" style={{ fontSize: "0.75rem", padding: "0.45rem 0.75rem" }} onClick={downloadCsv}>
          Download Audit Log
        </button>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.78rem" }}>
          <thead>
            <tr style={{ textAlign: "left", color: "var(--ed-text-muted)" }}>
              <th style={{ padding: "0.35rem 0.5rem" }}>Timestamp</th>
              <th style={{ padding: "0.35rem 0.5rem" }}>Action</th>
              <th style={{ padding: "0.35rem 0.5rem" }}>Item</th>
              <th style={{ padding: "0.35rem 0.5rem" }}>User</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <tr key={row.id} style={{ borderTop: "1px solid rgba(148, 163, 184, 0.22)" }}>
                <td style={{ padding: "0.45rem 0.5rem", whiteSpace: "nowrap", color: "var(--ed-text-secondary)" }}>
                  {row.created_at ? new Date(row.created_at).toLocaleString("en-ZA", { dateStyle: "short", timeStyle: "short" }) : "—"}
                </td>
                <td style={{ padding: "0.45rem 0.5rem", color: "var(--ed-text)" }}>{row.action}</td>
                <td style={{ padding: "0.45rem 0.5rem", color: "var(--ed-text-secondary)" }}>{row.entity_id ? String(row.entity_id).slice(0, 8) : "—"}</td>
                <td style={{ padding: "0.45rem 0.5rem", color: "var(--ed-text-secondary)" }}>{row.user_id ? String(row.user_id).slice(0, 8) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.4rem", marginTop: "0.5rem" }}>
        <button type="button" className="ed-btn-ghost" style={{ fontSize: "0.72rem", padding: "0.35rem 0.55rem" }} disabled={boundedPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
          Prev
        </button>
        <span style={{ fontSize: "0.72rem", color: "var(--ed-text-muted)", alignSelf: "center" }}>
          Page {boundedPage} / {pageCount}
        </span>
        <button type="button" className="ed-btn-ghost" style={{ fontSize: "0.72rem", padding: "0.35rem 0.55rem" }} disabled={boundedPage >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>
          Next
        </button>
      </div>
    </div>
  );
}
